/**
 * `defineMessages` and `catalogOf` (W1-12 ui-kit-i18n; contract src/i18n/types.ts, ARCHITECTURE.md §13).
 *
 * LEAF module: imports only ./types; no glob, no registry, no side effects. Every UI `*.messages.ts` calls these
 * at module load:
 *
 *   export const m = defineMessages('notes', { placeTap: 'Place a Tap', count: '{n, plural, one {# note} other {# notes}}' });
 *   export default catalogOf(m);
 *
 * The returned object maps each id to its namespaced key ('notes.placeTap'); the English catalogue rides along
 * as a non-enumerable `__catalog` property, so `Object.keys(m)` lists the ids only. The runtime reads the default
 * export of the file (the catalogue) through its glob; nothing is registered here.
 */

import type { CatalogOf, DefineMessages, DefinedMessages, MessageCatalog, MessageKey } from './types';

export const defineMessages: DefineMessages = <M extends Readonly<Record<string, string>>>(ns: string, english: M) => {
  const keys: Record<string, MessageKey> = {};
  for (const id of Object.keys(english)) keys[id] = `${ns}.${id}` as MessageKey;
  const catalog: MessageCatalog = Object.freeze({ ns, locale: 'en', messages: Object.freeze({ ...english }) });
  Object.defineProperty(keys, '__catalog', { value: catalog, enumerable: false });
  return Object.freeze(keys) as unknown as DefinedMessages<M>;
};

export const catalogOf: CatalogOf = (m) => m.__catalog;
